import React, { useMemo, useState } from 'react';
import { useFieldHelper, FieldProps } from 'framework/ui/field';
import { EditableBadge, EditableBadgeValueProps } from 'framework/ui/ui-builder';
import { RECRUITING_VACANCY_GROUP_UPDATE, VacancyStatus } from 'domain/shared/newVacancy';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { useAuthorization } from 'framework/authorization';
import useVacancyPublicationService from '../../vacancyPublicationService';
import { useVacancyContext } from 'domain/recruiting/newVacancy/VacancyContext';
import { ChipOwnProps } from '@mui/material';

const VacancyStatusColor: Record<string, ChipOwnProps['color']> = {
  [VacancyStatus.OPEN]: 'success',
  [VacancyStatus.DRAFT]: 'primary',
  [VacancyStatus.CLOSED]: 'default',
};

const VacancyStatuses = [
  {
    value: VacancyStatus.OPEN,
    bgcolor: 'background.success',
    menuOptions: [VacancyStatus.CLOSED],
  },
  {
    value: VacancyStatus.DRAFT,
    bgcolor: 'background.primary',
    menuOptions: [VacancyStatus.CLOSED, VacancyStatus.OPEN],
  },
  {
    value: VacancyStatus.CLOSED,
    bgcolor: 'background.default',
    textColor: 'text.primary',
    menuOptions: [VacancyStatus.OPEN],
  },
];

interface StatusBadgeProps extends FieldProps {
  statusSource: string;
  idSource: string;
}

const StatusBadge = ({ idSource, statusSource }: StatusBadgeProps) => {
  const { getValue: getStatus } = useFieldHelper({ source: statusSource });
  const { getValue: getId } = useFieldHelper({ source: idSource });
  const { isGranted } = useAuthorization();
  const [status, setStatus] = useState<VacancyStatus>(getStatus() as VacancyStatus);
  const { updateStatus } = useVacancyPublicationService();
  const { arePublicationsUpdated, setArePublicationsUpdated } = useVacancyContext();

  const handleChange = async (newStatus: string) => {
    const vacancy = await updateStatus(getId(), newStatus);
    setStatus(vacancy.status);

    if (setArePublicationsUpdated) {
      setArePublicationsUpdated(!arePublicationsUpdated);
    }
  };

  const statuses: EditableBadgeValueProps[] = useMemo(() => {
    const findStatusMenuOptions = VacancyStatuses.find(({ value }) => value === status)?.menuOptions || [];
    const result = findStatusMenuOptions.reduce((arr, option) => {
      const st = VacancyStatuses.find(({ value }) => value === option);
      if (st) {
        arr.push(st);
      }
      return arr;
    }, [] as EditableBadgeValueProps[]);
    return result;
  }, [status]);

  return (
    <EditableBadge
      color={VacancyStatusColor[status]}
      values={statuses}
      onChange={handleChange}
      canUpdate={isGranted(RECRUITING_VACANCY_GROUP_UPDATE, RECRUITING_CONTEXT)}
      badgeSx={{ padding: '4px', border: '1px solid', borderColor: 'border.contrast' }}
      menuItemSx={{
        margin: '4px',
        borderRadius: '4px',
        color: 'common.white',
      }}
    >
      {status}
    </EditableBadge>
  );
};

export default StatusBadge;
